import React from "react";
import { localizeWithMemberName } from "@/main/react/component/localizeWithMemberName";
import { Localizer } from "@/main/react/Types";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import InputWithCharCounter from "@/main/react/component/InputWithCharCounter";
import { EmailTypoSuggestion } from "@/main/react/component/EmailTypoSuggestion";
import { useEmailTypoSuggestion } from "@/main/react/hooks/useEmailTypoSuggestion";
import { ClientFactory } from "@/main/common/ClientFactory";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { isHandledAuthSessionError } from "@/main/common/AjaxAuthSessionHandler";
import { logApiFailure } from "@/main/common/ApiUtils";

/**
 * Where a subscribe happened (newsletter growth work order A24). Sent as the optional `?surface=` on both the
 * member `subscribe` and the visitor `subscribeExternal` request (slice 2a added the param; wired at the wave-2
 * landing). The server drops any value outside this set, so the union here must stay in step with
 * `NewsletterService`'s allowlist.
 */
export type NewsletterSubscribeSurface =
    | "newsletter_page"
    | "issue_page"
    | "post_page"
    | "profile"
    | "email_footer"
    | "bell";

export type NewsletterSubscribeControlProps = {
    publicId?: string;
    ownerUsername?: string;
    initiallySubscribed?: boolean;
    localizer: Localizer;
    /** Members subscribe in one tap; visitors subscribe with their email (double opt-in). */
    loggedIn: boolean;
    surface?: NewsletterSubscribeSurface;
    /**
     * Called with the member's new state as soon as the toggle flips (optimistically), and again with the
     * old state if the request fails. The newsletter page keeps its subscriber count in step with it.
     */
    onSubscribedChange?: (subscribed: boolean) => void;
};

/**
 * The reader's subscribe control, extracted from `NewsletterPage` with no behaviour change (work order A11,
 * slice 1c): a one-tap toggle for a signed-in member, and for a visitor an email form that ends in a calm
 * "check your inbox" confirmation whatever the server answered. Never rendered for the owner.
 *
 * Self-contained on purpose: wave 2 mounts it on the issue page, under posts and on the profile, so it owns
 * its own request state and reads nothing from the page around it. It renders its parts as siblings (no
 * wrapper), so the caller's flex row lays them out exactly as before the extraction.
 */
export function NewsletterSubscribeControl({
    publicId,
    ownerUsername,
    initiallySubscribed = false,
    localizer,
    loggedIn,
    surface,
    onSubscribedChange,
}: NewsletterSubscribeControlProps) {
    const [subscribed, setSubscribed] = React.useState<boolean>(initiallySubscribed);
    const [busy, setBusy] = React.useState(false);
    const [email, setEmail] = React.useState("");
    const [emailSubmitting, setEmailSubmitting] = React.useState(false);
    const [emailSubmitted, setEmailSubmitted] = React.useState(false);
    const emailTypo = useEmailTypoSuggestion();

    async function toggleSubscribe() {
        if (busy || !publicId) return;
        setBusy(true);
        const wasSubscribed = subscribed;
        setSubscribed(!wasSubscribed);
        onSubscribedChange?.(!wasSubscribed);
        try {
            if (wasSubscribed) {
                await ClientFactory.getAjaxNewsletterRestControllerApi().unsubscribe(publicId);
                toast.success(localizer("newsletter.unsubscribe.success"));
            } else {
                await ClientFactory.getAjaxNewsletterRestControllerApi().subscribe(publicId, surface);
                toast.success(localizer("newsletter.subscribe.success"));
            }
        } catch (err: any) {
            setSubscribed(wasSubscribed);
            onSubscribedChange?.(wasSubscribed);
            if (isHandledAuthSessionError(err)) {
                return;
            }
            logApiFailure("Failed toggling newsletter subscription", err);
            toast.error(localizer("newsletter.action.failed"));
        } finally {
            setBusy(false);
        }
    }

    async function submitExternalSubscribe() {
        if (emailSubmitting || !publicId) return;
        const trimmed = email.trim();
        if (!trimmed || !trimmed.includes("@")) {
            toast.error(localizer("newsletter.subscribe.email_invalid"));
            return;
        }
        setEmailSubmitting(true);
        try {
            await ClientFactory.getAjaxNewsletterRestControllerApi().subscribeExternal(publicId, { email: trimmed }, surface);
            // Generic ack: always show the same calm "check your inbox" outcome.
            setEmailSubmitted(true);
        } catch (err: any) {
            if (isHandledAuthSessionError(err)) return;
            logApiFailure("Failed external newsletter subscribe", err);
            toast.error(localizer("newsletter.subscribe.email_failed"));
        } finally {
            setEmailSubmitting(false);
        }
    }

    if (loggedIn) {
        return (
            <CtaButton
                variant={subscribed ? "outline" : "primary"}
                type="button"
                onClick={() => void toggleSubscribe()}
                disabled={busy}
                isLoading={busy}
                text={subscribed ? localizer("newsletter.subscribed") : localizer("newsletter.subscribe")}
                icon={Mail}
                playwrightId={"newsletter-subscribe-toggle"}
            />
        );
    }

    if (emailSubmitted) {
        return (
            <div
                className="rounded-2xl border border-border bg-[var(--background2)] px-4 py-3"
                data-playwright-id="newsletter-subscribe-email-confirm"
            >
                <div className="text-sm font-medium text-text-main">
                    {localizer("newsletter.subscribe.check_inbox_title")}
                </div>
                <div className="mt-0.5 text-xs text-neutral-text">
                    {localizer("newsletter.subscribe.check_inbox_body")}
                </div>
            </div>
        );
    }

    return (
        <form
            className="flex w-full max-w-md flex-wrap items-center gap-2"
            onSubmit={(e) => {
                e.preventDefault();
                void submitExternalSubscribe();
            }}
        >
            <InputWithCharCounter
                id="newsletter-subscribe-email"
                type="email"
                value={email}
                onChange={(e) => {
                    setEmail(e.target.value);
                    emailTypo.clear();
                }}
                onBlur={() => emailTypo.evaluate(email)}
                disabled={emailSubmitting}
                floatingLabel={localizer("newsletter.subscribe.email_placeholder")}
                hideCharCounter
                wrapperClassName="min-w-0 flex-1"
                data-playwright-id="newsletter-subscribe-email"
            />
            <CtaButton
                variant={"primary"}
                type="submit"
                disabled={emailSubmitting}
                isLoading={emailSubmitting}
                text={localizer("newsletter.subscribe.email_cta")}
                icon={Mail}
                playwrightId={"newsletter-subscribe-email-submit"}
            />
            {/* Full-width so the flex row wraps and the nudge sits BELOW the
                field rather than squeezing beside it. */}
            <div className="w-full">
                <EmailTypoSuggestion
                    localizer={localizer}
                    suggestion={emailTypo.suggestion}
                    playwrightId="newsletter-subscribe-email-typo-suggestion"
                    onAccept={(corrected) => {
                        setEmail(corrected);
                        // clear(), never dismiss(): dismiss() suppresses the value
                        // it is GIVEN, and the value in scope here is the mistyped
                        // one. See §9.1 of the design doc.
                        emailTypo.clear();
                    }}
                    onDismiss={() => emailTypo.dismiss(email)}
                />
            </div>
            {ownerUsername && (
                <p
                    className="w-full text-xs text-neutral-text"
                    data-playwright-id="newsletter-subscribe-author-sees-email"
                >
                    {localizeWithMemberName(localizer, "newsletter.subscribe.author_sees_email", ownerUsername)}
                </p>
            )}
        </form>
    );
}

export default NewsletterSubscribeControl;
